/**
 * SocialLinks
 *
 * Renders a row (or column) of social-platform tiles for any handles the user
 * has set. Each tile = a perfectly round logo circle + the @handle next to it.
 * Tapping the tile opens the user's profile on that platform in a new tab.
 *
 * Used on:
 *   • UserDetailModal — right side of the profile info row
 *   • ProfileScreen   — under the banner, right-aligned
 */
import React from 'react';

export interface SocialsShape {
  instagram?: string;
  snapchat?: string;
  tiktok?: string;
  x?: string;
}

interface SocialLinksProps {
  socials?: SocialsShape;
  /** Icon circle diameter in px. Default 32. */
  size?: number;
  className?: string;
  /** Direction. Default 'col'. */
  direction?: 'row' | 'col';
  /** Gap between tiles in px. Default 8. */
  gap?: number;
  /** Show the @handle text next to each icon. Default true. */
  showHandle?: boolean;
}

const stripAt = (s: string) => s.replace(/^@+/, '').trim();

const urls = {
  instagram: (h: string) => `https://www.instagram.com/${stripAt(h)}`,
  snapchat:  (h: string) => `https://www.snapchat.com/add/${stripAt(h)}`,
  tiktok:    (h: string) => `https://www.tiktok.com/@${stripAt(h)}`,
  x:         (h: string) => `https://x.com/${stripAt(h)}`,
};

const brand = {
  instagram: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
  snapchat:  '#FFFC00',
  tiktok:    '#000000',
  x:         '#000000',
};

/**
 * Each glyph uses a full 24×24 viewBox so it sits perfectly centred inside the
 * round button regardless of size. The icon takes 55 % of the circle diameter.
 */
const Glyph: React.FC<{ kind: keyof SocialsShape; iconColor: string }> = ({ kind, iconColor }) => {
  const style: React.CSSProperties = { width: '55%', height: '55%', display: 'block' };

  if (kind === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={style} aria-hidden>
        <rect x="3" y="3" width="18" height="18" rx="5" stroke={iconColor} strokeWidth="2"/>
        <circle cx="12" cy="12" r="4" stroke={iconColor} strokeWidth="2"/>
        <circle cx="17.5" cy="6.5" r="1.1" fill={iconColor}/>
      </svg>
    );
  }
  if (kind === 'snapchat') {
    return (
      <svg viewBox="0 0 24 24" fill={iconColor} style={style} aria-hidden>
        <path
          d="M12 2c-3.2 0-5.5 2.3-5.5 5.5 0 1 .1 2 .1 2.6-.4.3-1 .5-1.5.5-.4 0-.7.2-.7.6 0 .5.6.9 1.4 1.2.8.3 1.7.6 1.7 1.1 0 .7-1.6 2.3-3.5 2.8-.4.1-.7.4-.5.8.4.7 1.4 1.1 2.4 1.2.1.4.3 1 .7 1.1.3.1.7 0 1.1-.1.7-.2 1.7-.2 2.3.1.7.4 1.4.9 2.5.9s1.8-.5 2.5-.9c.6-.3 1.6-.3 2.3-.1.4.1.7.2 1.1.1.4-.1.6-.7.7-1.1 1-.1 2-.5 2.4-1.2.2-.4-.1-.7-.5-.8-1.9-.5-3.5-2.1-3.5-2.8 0-.5.9-.8 1.7-1.1.8-.3 1.4-.7 1.4-1.2 0-.4-.3-.6-.7-.6-.5 0-1.1-.2-1.5-.5 0-.6.1-1.6.1-2.6C17.5 4.3 15.2 2 12 2z"
          stroke="#000000"
          strokeWidth="0.6"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (kind === 'tiktok') {
    return (
      <svg viewBox="0 0 24 24" fill={iconColor} style={style} aria-hidden>
        <path d="M15.5 2c.2 1.7 1.2 3 2.8 3.6.7.3 1.3.4 2 .4v3.2c-1.4 0-2.7-.4-3.9-1.1v6c0 3.5-2.8 6.3-6.3 6.3S3.8 17.6 3.8 14.1s2.8-6.3 6.3-6.3c.3 0 .7 0 1 .1v3.4c-.3-.1-.7-.2-1-.2-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3V2h2.4z"/>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill={iconColor} style={style} aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.65l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.25 2.25h6.812l4.713 6.231zM17.084 19.77h1.833L7.084 4.126H5.117L17.084 19.77z"/>
    </svg>
  );
};

export const SocialLinks: React.FC<SocialLinksProps> = ({
  socials,
  size = 32,
  className = '',
  direction = 'col',
  gap = 8,
  showHandle = true,
}) => {
  if (!socials) return null;

  const entries: Array<{ kind: keyof SocialsShape; handle: string }> = (
    ['instagram', 'snapchat', 'tiktok', 'x'] as const
  )
    .map(kind => ({ kind, handle: (socials[kind] || '').trim() }))
    .filter(e => e.handle.length > 0);

  if (entries.length === 0) return null;

  return (
    <div
      className={className}
      style={{
        display: 'inline-flex',
        flexDirection: direction === 'col' ? 'column' : 'row',
        gap,
        alignItems: direction === 'col' ? 'flex-start' : 'center',
      }}
    >
      {entries.map(({ kind, handle }) => {
        const cleanHandle = stripAt(handle);
        // Every glyph is white — the Snapchat ghost included (sits on yellow).
        const iconColor = '#ffffff';
        const targetUrl = urls[kind](handle);
        // Use a <button> instead of <a target="_blank">: iOS Capacitor WKWebView
        // ignores target="_blank" (no popup support), and React/Tailwind
        // sometimes-stacks parent overlays that swallow the link's default
        // navigation. A button + explicit window.open works the same on web,
        // Capacitor, and PWA shells.
        const handleClick = (e: React.MouseEvent | React.PointerEvent) => {
          e.stopPropagation();
          // window.open(url, '_blank') — no features arg.
          // Passing a third `features` string (even "noopener,noreferrer") makes
          // browsers treat the call as a popup-window request, which the popup
          // blocker then kills (returns null). Omitting features opens a real
          // new tab. We deliberately do NOT fall back to window.location.href
          // because navigating the current tab would yank the user out of the
          // app — preferable to do nothing than to clobber their session.
          try {
            window.open(targetUrl, '_blank');
          } catch (err) {
            console.warn('[SocialLinks] window.open failed', err);
          }
        };
        return (
          <button
            key={kind}
            type="button"
            onClick={handleClick}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={`Open @${cleanHandle} on ${kind === 'x' ? 'X' : kind.charAt(0).toUpperCase() + kind.slice(1)}`}
            title={`@${cleanHandle}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
              textDecoration: 'none',
              color: 'inherit',
              minWidth: 0,
              cursor: 'pointer',
              pointerEvents: 'auto',
              touchAction: 'manipulation',
            }}
          >
            {/* The round logo circle — perfectly centred glyph */}
            <span
              style={{
                width: size,
                height: size,
                minWidth: size,
                minHeight: size,
                borderRadius: '50%',
                background: brand[kind],
                boxShadow: '0 2px 6px rgba(0,0,0,0.20)',
                border: kind === 'snapchat' ? '1px solid rgba(0,0,0,0.18)' : 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
                transition: 'transform 0.15s ease',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLSpanElement).style.transform = 'scale(1.06)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLSpanElement).style.transform = 'scale(1)'; }}
            >
              <Glyph kind={kind} iconColor={iconColor} />
            </span>
            {showHandle && (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 160,
                }}
                className="text-black dark:text-white"
              >
                @{cleanHandle}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default SocialLinks;
